import axios from 'axios';

const TEMPLATES_URL = '/api/templates';
const INDUSTRIES_URL = '/api/industries';

/**
 * Fetch all templates, optionally filtered by domain.
 * @param {string|null} domain - e.g. "Healthcare", "Finance"
 * @param {boolean|null} isSystem
 * @param {string|null} templateClass - "system" | "starter" | "promoted"
 * @returns {Promise<Array>}
 */
export async function fetchTemplates(domain = null, isSystem = null, templateClass = null) {
  const params = {};
  if (domain) params.domain = domain;
  if (isSystem !== null) params.is_system = isSystem;
  if (templateClass) params.template_class = templateClass;
  const response = await axios.get(TEMPLATES_URL, { params });
  return response.data;
}

/**
 * Delete a user-created template.
 * @param {string} templateId
 * @returns {Promise<void>}
 */
export async function deleteTemplate(templateId) {
  await axios.delete(`${TEMPLATES_URL}/${templateId}`);
}

/**
 * Fetch a single template with full detail (including graph_structure).
 * @param {string} templateId
 * @returns {Promise<Object>}
 */
export async function fetchTemplateDetail(templateId) {
  const response = await axios.get(`${TEMPLATES_URL}/${templateId}`);
  return response.data;
}

/**
 * Fetch the industry registry.
 * @returns {Promise<Array>} Array of {key, label, description, icon}
 */
export async function fetchIndustries() {
  const response = await axios.get(INDUSTRIES_URL);
  return response.data?.industries || [];
}

/**
 * Save a completed workflow as a reusable template.
 * @param {string} workflowId
 * @param {string} name - Template name
 * @param {string|null} description - Optional description
 * @returns {Promise<Object>} Saved template
 */
export async function saveWorkflowAsTemplate(workflowId, name, description = null) {
  const response = await axios.post(`/api/workflows/${workflowId}/save-as-template`, {
    name,
    description,
  });
  return response.data;
}

/**
 * Instantiate a full-graph template with parameter values and execute immediately.
 * Skips decomposition — the graph is already defined.
 *
 * @param {string} templateId - AIWorkflowTemplate UUID
 * @param {Object} inputValues - {field_name: value} from input_schema form
 * @param {Object} [opts] - Optional overrides
 * @param {string}  [opts.taskDescription]
 * @param {string}  [opts.company]
 * @param {string}  [opts.domain]
 * @param {string}  [opts.role]
 * @param {Object}  [opts.constraints]        - e.g. { model_preference: 'quality_first' }
 * @param {number}  [opts.maxDynamicDepth]    - sub-agent spawn depth (0 = none)
 * @param {number}  [opts.maxDynamicWidth]    - max children per spawning node
 * @param {number}  [opts.maxRuns]            - max retry attempts per node
 * @param {number}  [opts.qualityThreshold]   - self-eval pass threshold (0–1)
 * @param {boolean} [opts.execute]            - false = clone only, no execution
 * @returns {Promise<{workflow_id, status, total_nodes, estimated_cost, graph, workflow}>}
 */
export async function instantiateTemplate(templateId, inputValues = {}, opts = {}) {
  const response = await axios.post(`/api/templates/${templateId}/instantiate`, {
    input_values: inputValues,
    task_description: opts.taskDescription || null,
    company: opts.company || null,
    domain: opts.domain || null,
    role: opts.role || null,
    constraints: opts.constraints || null,
    max_dynamic_depth: opts.maxDynamicDepth ?? 0,
    max_dynamic_width: opts.maxDynamicWidth ?? 3,
    max_runs: opts.maxRuns ?? 1,
    quality_threshold: opts.qualityThreshold ?? 0.8,
    execute: opts.execute !== false, // default true; pass execute:false to clone-only
  });
  return response.data;
}
